import { useCallback } from 'react';
import { useConfigInputs, useConfigActions, useVendorCtx } from 'shared/state/configContexts';
import { useAbility, PERM } from 'shared/access';
import { resolveSubscriptionOptions, type Option } from './subscriptionOptions';

export type { Option };

export function useSubscriptionSection() {
  const { config } = useConfigInputs();
  const { updateConfig } = useConfigActions();
  const { vendor } = useVendorCtx();
  const ability = useAbility();

  const allQuarters = ability.can(PERM.QUARTER_ALL);
  const specialTerms = ability.can(PERM.ADMIN_SKUS);
  const { quarterOptions, monthOptions, serviceOptions } = resolveSubscriptionOptions(
    vendor.capabilities,
    config,
    { allQuarters, specialTerms },
  );

  // A vendor that declares its own terms sells exactly those, to every tier
  // (SMC's term licensing) — used only for the hint copy below.
  const declared: number[] | undefined = vendor.capabilities.subscriptionDurations as number[] | undefined;
  // The active selection is always present in `serviceOptions` (see
  // resolveSubscriptionOptions' hidden-service rule), so this mirrors the
  // classic lookup without re-reading the vendor's full, unfiltered list.
  const serviceLabel = serviceOptions.find((s) => s.value === config.serviceOption)?.label ?? config.serviceOption;

  const onQuarterChange = useCallback((value: string) => updateConfig({ quarter: value }), [updateConfig]);
  const onMonthsChange = useCallback((value: string) => updateConfig({ subscriptionMonths: parseInt(value, 10) }), [updateConfig]);
  const onServiceChange = useCallback((value: string) => updateConfig({ serviceOption: value }), [updateConfig]);

  return {
    quarter: config.quarter,
    quarterOptions,
    quarterGated: !allQuarters,
    onQuarterChange,
    months: String(config.subscriptionMonths ?? 0),
    monthOptions,
    onMonthsChange,
    serviceOption: config.serviceOption,
    serviceOptions,
    onServiceChange,
    termHint: declared
      ? 'This vendor sells fixed terms; software is quoted against the same term.'
      : '12 and 24-month terms require special access.',
    summary: `${config.quarter} · ${config.subscriptionMonths ?? 0} mo · ${serviceLabel}`,
    // Subscription always has a default quarter, term and service — it can never be empty.
    status: 'ok' as const,
  };
}
